import { Button, Icon, Tag, Text } from "@ttc3k/trekker";
import { Card } from "@ttc3k/trekker";
import { Box } from "styled-system/jsx";
import { styled } from "styled-system/jsx";
import { EventManyAppQuery, EventWithOccurrences } from "gql/generated";
import { getMultilingualString, isLandscape } from "shared/utils";
import { Flex } from "styled-system/jsx";
import { useEventWidgetCard } from "../EventWidgetCard/useEventWidgetCard";
import { eventOperatorCardRecipe } from "./recipe";
import { Repeat } from "iconoir-react";
import { CloudinaryImage } from "shared/components";
import { css } from "styled-system/css";
import { scale } from "@cloudinary/transformation-builder-sdk/actions/resize";
import { useTranslation } from "react-i18next";
import { es, fr } from "date-fns/locale";
import { MultilingualLangKey } from "shared/types";

type EventOperatorCardProps = {
  event: EventManyAppQuery["eventMany"][0];
  onEditClick: () => void;
  borderStyle?: "solid" | "dashed";
};

export const EventOperatorCard = ({
  event,
  onEditClick,
  borderStyle = "solid",
}: EventOperatorCardProps) => {
  const { t, i18n } = useTranslation("directory", {
    keyPrefix: "OPERATOR.EVENTS.CTA_BUTTON",
  });
  const classes = eventOperatorCardRecipe({ borderStyle });
  const { dateInfo } = useEventWidgetCard(
    event as EventWithOccurrences,
    i18n.language as MultilingualLangKey,
    i18n.language === "fr" ? fr : i18n.language === "es" ? es : undefined,
  );

  const heroImage = event.images[0];
  const heroIsLandscape = isLandscape(heroImage);
  const heroHasText = heroImage?.detectedObjects?.hasText;

  /**
   * For landscape cards, the images are square, so we scale them by their height (landscape) or width (portrait)
   * - Cloudinary will pick the "best" part of the image to display
   * - If the image has text, it will be set to contain
   * If the image has text, we scale it to the width or height (the widget is a guess as the card grows in width based on screen size)
   * For non-text images, we use the limitFill to have Cloudinary pick the "best" part of the image to display
   * @returns
   */
  const getHeroResize = () =>
    heroIsLandscape ? scale().height(180) : scale().width(180);

  return (
    <Card.Surface className={classes.surface}>
      <Card.Body className={classes.body}>
        <Flex flexDir="column" justifyContent="space-between" gap="50" h="full">
          <Flex className={classes.dateAndTitle}>
            <Tag visual="primary" mode="light">
              <styled.span
                display="flex"
                flexDir="row"
                alignItems="center"
                gap="100"
              >
                <span>
                  {dateInfo.startDate}
                  {dateInfo.otherDates.length > 0 && (
                    <span>
                      {dateInfo.otherDates.slice(0, 2).map((date) => (
                        <span key={date}>
                          <styled.span color="text.light"> • </styled.span>
                          {date}
                        </span>
                      ))}
                      {dateInfo.otherDates.length > 2 && "..."}
                    </span>
                  )}
                </span>
                {dateInfo.showRepeat && <Icon Element={Repeat} />}
              </styled.span>
            </Tag>

            <Text
              visual={"bodySemiBold"}
              lineClamp={2}
              color={"text.dark"}
              h="48px"
            >
              {getMultilingualString(event.title)}
            </Text>
          </Flex>
          <Button
            visual="primary"
            mode="light"
            onClick={onEditClick}
            w="fit-content"
          >
            {t("EDIT")}
          </Button>
        </Flex>
      </Card.Body>
      <Box part={"card__hero"} className={classes.hero}>
        <CloudinaryImage
          includeBgBlurHash={true}
          image={heroImage}
          resize={getHeroResize()}
          imageStyleProps={{
            className: css({
              backgroundColor: "#FFF",
              aspectRatio: "square",
              objectFit: heroHasText ? "contain" : "cover",
              h: "180px",
              w: "180px",
              borderRadius: 0,
              border: "none !important",
            }),
          }}
        />
      </Box>
    </Card.Surface>
  );
};
